import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteGuidPrecheckResult } from '@pmp/api';
import { Modal, Spin, Typography, notification } from 'antd';
import { ConfirmCrDeleteModalProps } from './interfaces';
import styles from './styles.module.css';
import { useFeatureToggle } from '@pmp/hooks';
import { changeRequestNotificationMessage } from '@pmp/utils';

const { Text } = Typography;

export const ConfirmCrDeleteModal = <T,>({
  open,
  records,
  idKey,
  nameKey,
  entityLabel,
  entityLabelPlural,
  firmId,
  refreshTable,
  getPrecheck,
  getFirmPrecheck,
  createDeleteChangeRequest,
  createFirmDeleteChangeRequest,
  onClose,
}: ConfirmCrDeleteModalProps<T>) => {
  const [data, setData] = useState<DeleteGuidPrecheckResult | null>(null);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const { canDelete, notFound, blocked } = data || { canDelete: [], notFound: [], blocked: [] };
  const enableFourEyes = useFeatureToggle(['enableFourEyes']);

  const ids = useMemo(() => records?.map(record => record[idKey] as string) || [], [idKey, records]);

  const labels = useMemo(
    () =>
      records?.reduce(
        (acc, record) => {
          const key = record[idKey] as string;
          const value = record[nameKey] !== undefined && record[nameKey] !== null ? (record[nameKey] as string) : key;
          return { ...acc, [key]: value };
        },
        {} as Record<string, string>,
      ) || {},
    [idKey, nameKey, records],
  );

  useEffect(() => {
    if (!open) {
      if (data) setData(null);
      return;
    }
    if (data) return;

    if (firmId && getFirmPrecheck) {
      getFirmPrecheck({ body: { ids }, path: { firmId } })
        .then(({ data }: { data: DeleteGuidPrecheckResult }) => {
          setData(data);
        })
        .catch(() => {
          notification().error({ message: t('global.errors.unexpected') });
          onClose();
        });
    }

    if (!firmId && getPrecheck) {
      getPrecheck({ body: { ids } })
        .then(({ data }: { data: DeleteGuidPrecheckResult }) => {
          setData(data);
        })
        .catch(() => {
          notification().error({ message: t('global.errors.unexpected') });
          onClose();
        });
    }
  }, [data, getPrecheck, getFirmPrecheck, ids, onClose, open, t, firmId]);

  const onOk = useCallback(() => {
    if (!data?.canDelete) {
      notification().error({ message: t('global.errors.unexpected') });
      onClose();
      return;
    }
    setConfirmLoading(true);

    const request =
      firmId && createFirmDeleteChangeRequest
        ? createFirmDeleteChangeRequest({ body: { ids: data?.canDelete }, path: { firmId } })
        : createDeleteChangeRequest
          ? createDeleteChangeRequest({ body: { ids: data?.canDelete } })
          : null;

    if (!request) {
      notification().error({ message: t('global.errors.unexpected') });
      setConfirmLoading(false);
      return;
    }

    request
      .then(() => {
        refreshTable();
        onClose();
        notification().success({
          message: changeRequestNotificationMessage(t, enableFourEyes),
        });
      })
      .catch(() => {
        notification().error({ message: t('global.errors.unexpected') });
        onClose();
      })
      .finally(() => {
        setConfirmLoading(false);
      });
  }, [createDeleteChangeRequest, createFirmDeleteChangeRequest, data?.canDelete, firmId, onClose, refreshTable, t, enableFourEyes]);

  return (
    <Modal
      title={t('global.labels.deleteX', { x: entityLabel })}
      open={open}
      onOk={onOk}
      confirmLoading={confirmLoading}
      onCancel={onClose}
      okButtonProps={{ danger: true, disabled: !data?.canDelete?.length }}
      okText={t('global.buttons.delete')}
    >
      <div className={styles.content}>
        {data ? (
          <>
            {!!notFound?.length && (
              <Text>
                {notFound?.length > 1
                  ? t('global.labels.deleteNotFoundXList', { x: entityLabelPlural })
                  : t('global.labels.deleteNotFoundX', { x: entityLabel })}
                <br />
                <ul>
                  {notFound.map(id => (
                    <li key={id}>
                      <i>{labels[id] || id}</i>
                    </li>
                  ))}
                </ul>
              </Text>
            )}
            {!!blocked?.length && (
              <Text>
                {t('global.labels.deleteBlockedX', { x: blocked?.length > 1 ? entityLabelPlural : entityLabel })}
                <br />
                <ul>
                  {blocked.map(item => (
                    <li key={item.id}>
                      <i>{labels[item.id] || item.id}</i>: {item.errors.join(' ')}
                    </li>
                  ))}
                </ul>
              </Text>
            )}
            {!!canDelete?.length && (
              <Text>
                {t('global.labels.confirmCrDeleteX', { x: canDelete?.length > 1 ? entityLabelPlural : entityLabel })}
                <br />
                <ul>
                  {canDelete?.map(id => (
                    <li key={id}>
                      <i>{labels[id] || id}</i>
                    </li>
                  ))}
                </ul>
              </Text>
            )}
          </>
        ) : (
          <Spin />
        )}
      </div>
    </Modal>
  );
};
